import {
  AdyenDropinContainerPico,
  AdyenDropinContainerPicoConfiguration,
} from "./adyen-dropin-container-pico";
import { renderToString } from "jsx-async-runtime";
import {
  API_BASE_ADDRESS,
  BRAND_HEADER,
  CULTURE_HEADER,
  IS_TEST_ENVIRONMENT,
  LIVE,
  PG_BRAND,
  PG_CULTURE,
  BASKET_ID,
  BASKET_VERSION,
  TEST,
  ADYEN_DROPIN_CONTAINER_PICO,
  HOST_CONTAINER,
  PAYMENTS_BASKET_WEB_COMPONENT,
  OMIT,
  INCLUDE,
  SPINNER,
  FORM_LOADING_SPINNER,
  BUNDLE_LOADING_SPINNER,
} from "../constants";
import HttpRequest from "../utils/HttpRequest";
import { Spinner, SpinnerProps } from "./spinner";
import { sanitise } from "../utils";
import { AdyenComponentSettingsResponse } from "../model/common/providers/adyen/AdyenComponentSettingsResponse";
import { AdyenComponentSettingsRequest } from "../model/common/providers/adyen/AdyenComponentSettingsRequest";

class PaymentsBasketWebComponent extends HTMLElement {
  static observedAttributes = [BASKET_ID, BASKET_VERSION];
  apiBaseUrl: string;
  sessionId: string;
  basketId: string;
  basketVersion: string;
  isTestEnvironment: boolean;
  brand: string;
  culture: string;
  hostContainerId: string;
  firstRender: boolean = true;
  constructor() {
    super();
  }

  async getComponentSettings(): Promise<AdyenComponentSettingsResponse> {
    const adyenProcessorUrl = this.apiBaseUrl.includes('localhost')
      ? 'http://localhost:9206/public'
      : `${this.apiBaseUrl}/adyen`;

    const headers = new Headers();
    headers.append(BRAND_HEADER, this.brand);
    headers.append(CULTURE_HEADER, this.culture);

    let credentials = OMIT as RequestCredentials;
    if (this.isTestEnvironment) {
      credentials = INCLUDE as RequestCredentials;
    }
    const url = `${adyenProcessorUrl}/componentsettings/`;
    const response = await HttpRequest.post<AdyenComponentSettingsRequest, AdyenComponentSettingsResponse>({
      url,
      credentials,
      brand: this.brand,
      culture: this.culture,
      maxAttempts: Number.MAX_SAFE_INTEGER,
      body: {
        basketId: this.basketId,
        version: this.basketVersion
      }
    });
    return response.data;
  }

  async connectedCallback() {
    this.firstRender = false;
    await this.mountComponent();
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (this.firstRender && oldValue === null)
      return;

    if ((name === BASKET_ID || name === BASKET_VERSION) && oldValue !== newValue) {
      await this.mountComponent();
    }
  }

  async mountComponent() {
    this.basketId = this.getAttribute(BASKET_ID);
    this.basketVersion = this.getAttribute(BASKET_VERSION);
    this.brand = this.getAttribute(PG_BRAND);
    this.culture = this.getAttribute(PG_CULTURE);
    this.apiBaseUrl = sanitise(this.getAttribute(API_BASE_ADDRESS));
    this.isTestEnvironment = this.getAttribute(IS_TEST_ENVIRONMENT) === "true";
    this.hostContainerId = `${HOST_CONTAINER}-${this.basketId}-${this.basketVersion}`;

    const html = <div id={this.hostContainerId}></div>;
    this.innerHTML = await renderToString(html);

    let environment: typeof LIVE | typeof TEST = TEST;
    if (!this.isTestEnvironment) {
      environment = LIVE;
    }

    let spinnerProps = this.getAttribute("spinner-props");

    if (!spinnerProps)
      spinnerProps = JSON.stringify({ cover: false, fixed: true });

    const spinner = document.createElement(SPINNER) as Spinner;
    document.getElementById(this.hostContainerId).hidden = true;
    spinner.id = FORM_LOADING_SPINNER;
    spinner.spinnerProps = {
      label: "Loading",
      size: "large",
      ...(JSON.parse(spinnerProps) as SpinnerProps),
    };
    document
      .getElementById(this.hostContainerId)
      .insertAdjacentElement("afterend", spinner);

    const componentsettings = await this.getComponentSettings();
    this.sessionId = componentsettings.sessionId;

    const adyenProcessorUrl = this.apiBaseUrl.includes('localhost')
      ? 'http://localhost:9206/public'
      : `${this.apiBaseUrl}/adyen`;

    const adyenDropinConfiguration: AdyenDropinContainerPicoConfiguration = {
      clientKey: componentsettings.adyenClientKey,
      environment: environment,
      sessionId: this.sessionId,
      brand: this.brand,
      culture: this.culture,
      apiBaseUrl: adyenProcessorUrl,
      hostContainerId: this.hostContainerId,
      countryCode: this.culture.split('-')[1] || '',
      additionalValidations: undefined,
      hasAdditionalValidation: false,
      ...componentsettings
    };

    let hostContainer = document.getElementById(this.hostContainerId);
    hostContainer.replaceChildren(); // Clear down the host container

    const adyenDropinContainer = document.createElement(
      ADYEN_DROPIN_CONTAINER_PICO
    ) as AdyenDropinContainerPico;    

    adyenDropinContainer.configuration = adyenDropinConfiguration;
    hostContainer.appendChild(adyenDropinContainer);

    const bundleSpinner = document.getElementById(BUNDLE_LOADING_SPINNER);
    if (!!bundleSpinner) {
      bundleSpinner.hidden = true;
    }
  }
}

if (!customElements.get(PAYMENTS_BASKET_WEB_COMPONENT)) {
  customElements.define(PAYMENTS_BASKET_WEB_COMPONENT, PaymentsBasketWebComponent);
}
